<template>
  <article>
    <activity-stage
      :customRootContainerClass="`label-maker-activity ${keyboardEnabled ? '' : 'full-height'}`"
      :customContentContainerStyles="{
        minWidth: '100%',
        width: 'max-content'
      }"
    >
      <template slot="instructions">
        <slot name="instructions" :onVoiced="handleOnInstructionsVoiced"></slot>
      </template>
      <section class="label-maker-container" slot="content">
        <section class="draggable-words-container">
          <div
            :aria-label="`label for ${step.answer}`"
            class="label-word"
            v-for="(step, index) of currentRound.steps"
            :key="step.id"
            :class="`image step-${step.id}`"
            aria-pressed="false"
            role="button"
            tabindex="0"
            @keyup.enter="handleOnLabelWordEnterEvent(`step-${step.id}`)"
          >
            <svg height="46px" width="220px" class="border-style" :style="cardBorder"></svg>

            <p v-if="!finishedSteps.some(({ id }) => id === step.id)">{{ step.answer }}</p>

            <blinking-cursor-text
              v-else
              :text="step.answer"
              :text-step="index"
            ></blinking-cursor-text>
          </div>
        </section>

        <section
          class="label-drop-zone-container"
          :style="{
            backgroundImage: `url(${currentRound.contentImage?.image.src})`
          }"
        >
          <div
            :aria-label="`label answer for ${step.answer}`"
            class="label-word-white focusable"
            v-for="step of currentRound.steps"
            :key="step.id"
            :class="`image step-${step.id}`"
            :style="{
              left: `${step.position.x - 110}px`,
              top: `${step.position.y - 130}px`
            }"
            @keyup.enter="handleOnLabelWordWhiteEnterEvent(`step-${step.id}`)"
          >
            <div class="label-word-position">
              <svg
                height="46px"
                width="220px"
                class="border-style-white"
                :style="cardBorderWhite"
              ></svg>

              <p>{{ step.answer.slice(0, 1) }}</p>
            </div>
          </div>
        </section>
      </section>
    </activity-stage>
    <article :style="{ display: keyboardEnabled ? 'block' : 'none' }">
      <slot name="keyboard"></slot>
    </article>
  </article>
</template>

<script>
import { TweenMax, TimelineLite } from 'gsap';
import Draggable from 'gsap/Draggable';

import AssetLoader from 'mixins/AssetLoader';
import AccessibilityHelper from 'mixins/AccessibilityHelper';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ActivityManager from 'mixins/ActivityManager';
import ErrorLogger from 'mixins/ErrorLogger';

import { hasTouchedBoundings, getActivityBoundings } from 'utils/activity-util';

import ActivityStage from 'components/activity/ActivityStage.vue';
import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';

import labelBorder from 'assets/svg/label-maker/label-maker-border.svg';
import labelBorderWhite from 'assets/svg/label-maker/label-maker-border-white.svg';

export default {
  components: {
    ActivityStage,
    BlinkingCursorText
  },
  mixins: [AssetLoader, TypingActivityHelper, ActivityManager, ErrorLogger, AccessibilityHelper],
  data() {
    return {
      activityName: 'Label Maker',
      cardBorder: {
        backgroundImage: `url('data:image/svg+xml;utf8,${labelBorder}')`
      },
      cardBorderWhite: {
        backgroundImage: `url('data:image/svg+xml;utf8,${labelBorderWhite}')`
      },
      currentKeyboardWord: '',
      droppables: '',
      inPosition: false,
      finishedSteps: [],
      draggableWords: []
    };
  },

  computed: {
    isEndOfActivity() {
      return this.finishedSteps.length === this.totalStepsInRound;
    }
  },

  beforeDestroy() {
    TweenMax.killAll();
  },

  methods: {
    handleOnInstructionsVoiced() {
      this.setScreenReaderAlert('Please switch to Focus mode to complete this activity.');
    },

    async loadActivity() {
      const roundSetup = this.getActivityState();
      this.createRounds(roundSetup);
      await this.loadImages();
      await this.loadSounds();
    },

    async loadImages() {
      this.rounds.forEach(({ contentImage }) => {
        this.queueImage(contentImage.image.src);
      });
      return this.downloadImages();
    },

    async loadSounds() {
      this.rounds.forEach(round => {
        round.steps.forEach(({ answerAudio }) => {
          this.queueAudio(answerAudio.audio.src);
        });
      });
      await this.downloadAudio();
    },

    getActivityState() {
      const { rounds } = this.activityData;

      return {
        rounds,
        instructions: {
          written: 'Drag and drop each label. Then type the word.',
          voice: `${this.voiceInstructionsPrefix}/thelabelmaker-${this.getRandomVoice()}-1.mp3`
        }
      };
    },

    startActivity() {
      this.setupNewRound();
      this.hideKeyboard();
      this.errorLoggerStart();

      TweenMax.set('.label-word', { autoAlpha: 1 });
      this.$nextTick(() => {
        this.setActivityContainerHeight();
      });

      this.$nextTick(() => {
        this.enableDragging();
      });
    },

    getStepIdForTarget(target) {
      const className = target.classList[2];
      return Number.parseInt(className.split('-')[1], 10);
    },

    enableDragging() {
      const self = this;
      const { isMobile } = this.userDevice;
      const tLite = new TimelineLite();
      let bounds = null;

      this.droppables = document.getElementsByClassName('label-word');

      const draggables = [...this.droppables].map(droppable => {
        let dragTimer = 0;
        let hasHitBoundary = false;
        const [draggable] = Draggable.create(droppable, {
          autoScroll: 1,
          type: 'x, y',
          throwProps: true,
          cursor: 'grab',
          bounds: '.label-maker-container',
          edgeResistance: isMobile ? 1.1 : 0.65,
          allowNativeTouchScrolling: false,
          onPress() {
            if (bounds) return;
            bounds = getActivityBoundings();
          },
          onDrag() {
            // work-around for iOS not firing touchend on tablet
            if (!isMobile) return;
            clearTimeout(dragTimer);
            const { pointerX, pointerY } = this;
            const hasHitBoundings = hasTouchedBoundings(pointerX, pointerY, bounds);
            if (hasHitBoundings && !hasHitBoundary) {
              dragTimer = setTimeout(() => {
                hasHitBoundary = true;
                self.handleDrop(this, this.target);
              }, 200);
            }
          },
          onDragStart() {
            tLite.set(droppable, { cursor: 'grabbing' });
          },
          onDragEnd() {
            clearTimeout(dragTimer);
            hasHitBoundary = false;
            self.handleDrop(this, this.target);

            if (self.inPosition) {
              const stepId = self.getStepIdForTarget(this.target);
              self.handleLabel(stepId);
            }
          }
        });
        return draggable;
      });

      this.draggableWords = draggables;
    },

    handleDrop(draggable, dragTarget) {
      const stepId = this.getStepIdForTarget(dragTarget);

      const keyGroupClassName = dragTarget.className.match(`step-${stepId}`);
      const [dragSource] = document.getElementsByClassName(`label-word-white ${keyGroupClassName}`);

      if (draggable.hitTest(dragSource, '50%')) {
        dragTarget.setAttribute('data-complete', 'complete');
        dragSource.setAttribute('data-complete', 'complete');
        const diffX =
          dragTarget.getBoundingClientRect().left - dragSource.getBoundingClientRect().left;
        const diffY =
          dragTarget.getBoundingClientRect().top - dragSource.getBoundingClientRect().top;
        const offsetX = Math.floor(draggable.x - diffX);
        const offsetY = Math.floor(draggable.y - diffY);

        TweenMax.to(dragTarget, 0.5, { x: offsetX, y: offsetY });

        draggable.disable();
        this.inPosition = true;
        this.resetSelectedLabelWordsStates(false);
        this.sndBell();
      } else {
        this.rerenderScreenReaderAlert('Incorrect, Try Again');
        this.sndError();

        draggable.disable();

        TweenMax.to(dragTarget, 0.5, {
          x: 0,
          y: 0,
          onComplete: draggable.enable.bind(draggable)
        });
      }
    },

    handleLabel(stepId) {
      const stepIndex = this.currentRound.steps.findIndex(step => step.id === stepId);
      this.draggableWords.map(dragItems => dragItems.disable());

      this.setCurrentStep({ step: stepIndex });
      this.enableKeyboard();
      this.showKeyboard({ enable: true });

      this.finishedSteps.push(this.currentStep);
      this.inPosition = false;
    },

    handleCorrectKey(attempt) {
      this.sndType();
      this.incrementTypingIndex();

      this.captureKeyboardAttemptSegment(attempt, this.isEndOfCurrentSentence);

      if (this.isEndOfCurrentSentence) {
        this.sndBell();
        this.finishStep();
      }
    },

    finishStep() {
      this.cache.get(this.currentStep.answerAudio.audio.src).play();
      this.hideKeyboard();
      this.completeStep();

      if (this.isEndOfActivity) {
        TweenMax.delayedCall(2, this.completeActivity);
        return;
      }

      this.resetSelectedLabelWordsStates(true);

      this.draggableWords.forEach(draggable => {
        const stepId = this.getStepIdForTarget(draggable.target);

        if (this.finishedSteps.some(step => step.id === stepId)) draggable.disable();
        else draggable.enable();
      });
    },

    // start keyboard accessible methods
    handleOnLabelWordEnterEvent(labelWordSelector) {
      const selectedLabelWordEl = document.getElementsByClassName(labelWordSelector)?.[0];

      const isLabelWordSelected = selectedLabelWordEl?.getAttribute('aria-pressed') !== 'false';

      this.resetSelectedLabelWordsStates(isLabelWordSelected);

      if (!isLabelWordSelected) {
        selectedLabelWordEl?.setAttribute('aria-pressed', true);
        selectedLabelWordEl?.setAttribute('tabindex', 0);
        selectedLabelWordEl?.classList.add('element-selected');
        selectedLabelWordEl?.focus();
      }

      this.setSelectableLabelWordsStates(!isLabelWordSelected);
    },

    handleOnLabelWordWhiteEnterEvent(labelWordWhiteSelector) {
      const { selectableLabelWordEl, selectedLabelWordEl } = this.getLabelMatchElements(
        labelWordWhiteSelector
      );

      if (selectedLabelWordEl) {
        selectedLabelWordEl.setAttribute('data-complete', 'complete');
        selectableLabelWordEl.setAttribute('data-complete', 'complete');
        const selectableLabelWordElCoordinates = selectableLabelWordEl.getBoundingClientRect();
        const selectedLabelWordElCoordinates = selectedLabelWordEl.getBoundingClientRect();
        const diffX = selectableLabelWordElCoordinates.left - selectedLabelWordElCoordinates.left;
        const diffY = selectableLabelWordElCoordinates.top - selectedLabelWordElCoordinates.top;

        selectedLabelWordEl.style.zIndex = 1000;

        TweenMax.to(selectedLabelWordEl, 0.5, {
          x: diffX,
          y: diffY
        });

        this.inPosition = true;
        this.handleLabel(+labelWordWhiteSelector.split('-')[1]);

        this.resetSelectableLabelWordsStates();
        this.resetSelectedLabelWordsStates(false);
        this.sndBell();
      } else {
        this.rerenderScreenReaderAlert('Incorrect, Try Again');
        this.sndError();
      }
    },

    resetSelectableLabelWordsStates() {
      Array.from(document.getElementsByClassName('label-word-white')).forEach(node => {
        node.blur();
        node.classList.remove('selectable-answer');
        node.setAttribute('tabindex', -1);
      });
    },

    resetSelectedLabelWordsStates(enableWords) {
      Array.from(document.getElementsByClassName('label-word')).forEach(node => {
        node.blur();
        node.classList.remove('element-selected');
        node.setAttribute('aria-pressed', false);
        node.setAttribute('tabindex', 0);
        node.classList.remove('disabled-label-word');
        if (node.getAttribute('data-complete') === 'complete' || !enableWords) {
          node.setAttribute('tabindex', -1);
          node.classList.add('disabled-label-word');
        }
      });
    },

    setSelectableLabelWordsStates(isHighlighted) {
      Array.from(document.getElementsByClassName('label-word-white')).forEach(node => {
        const isAlreadyComplete = node.getAttribute('data-complete') === 'complete';

        if (!isAlreadyComplete) node.classList.add('selectable-answer');
        if (!isHighlighted) node.classList.remove('selectable-answer');
        node.setAttribute('tabindex', isAlreadyComplete ? -1 : 0);
      });
    },

    getLabelMatchElements(labelWordWhiteSelector) {
      let selectableLabelWordEl;
      let selectedLabelWordEl;

      const labelWordEls = document.getElementsByClassName(labelWordWhiteSelector) || [];
      Array.from(labelWordEls).forEach(node => {
        if (node.classList.contains('selectable-answer')) {
          selectableLabelWordEl = node;
        }

        if (node.classList.contains('element-selected')) {
          selectedLabelWordEl = node;
        }
      });

      return { selectableLabelWordEl, selectedLabelWordEl };
    }
    // end keyboard accessible methods
  },

  watch: {
    finishedSteps(newVal) {
      if (newVal.length) {
        const instructions = `Type the phrase - ${newVal[newVal.length - 1].answer}`;

        if (instructions === this.currentScreenReaderAlert) {
          return;
        }

        this.rerenderScreenReaderAlert(instructions);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.label-maker-container {
  display: flex;
  flex-flow: column nowrap;
  width: 100%;

  .draggable-words-container {
    column-gap: 8px;
    display: flex;
    flex-flow: row wrap;
    justify-content: flex-start;
    max-width: 1024px;

    .label-word {
      box-sizing: border-box;
      height: 46px;
      position: relative;

      &:focus-visible {
        outline: 5px auto #015fcc !important;
      }

      &.disabled-label-word {
        cursor: auto !important;
      }
    }

    p {
      position: absolute;
      top: 12px;
      left: 20px;
    }

    .border-style {
      z-index: -1;
    }
  }

  .label-drop-zone-container {
    display: flex;
    position: relative;
    min-height: 330px;
    min-width: 1024px;
    width: max-content;
    height: max-content;
    background-size: auto;
    background-repeat: no-repeat;

    .label-word-white {
      box-sizing: border-box;
      position: absolute;
      width: 220px;
      height: 46px;

      .label-word-position {
        position: relative;
        width: 100%;
      }
    }

    p {
      position: absolute;
      top: 10px;
      left: 10px;
    }
  }
}

.label-maker-activity {
  .phoenix-keyboard-wrapper.keyboard-Keyboard35 {
    z-index: 1100;

    .MobileOnscreenKeyboard {
      .row {
        display: block;
      }
    }
  }
}

.tablet .label-maker-container {
  width: 100%;
  height: 100%;
}
</style>
